import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeftIcon,
  ChatBubbleLeftRightIcon,
  DevicePhoneMobileIcon,
  EnvelopeIcon,
  LockClosedIcon,
  PhoneIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { configAPI } from '../utils/api';

/**
 * Inscription, calquée sur le parcours de l'application mobile :
 *
 *   numéro → canal (SMS ou WhatsApp) → code → nom, genre, email, mot de passe
 *
 * Le numéro est vérifié avant toute création de compte, exactement comme dans
 * l'appli. Un numéro déjà connu bascule vers la connexion.
 */

const COUNTRY_CODES = [
  { code: '+228', label: 'Togo' },
  { code: '+229', label: 'Bénin' },
  { code: '+225', label: "Côte d'Ivoire" },
  { code: '+226', label: 'Burkina Faso' },
  { code: '+227', label: 'Niger' },
  { code: '+221', label: 'Sénégal' },
  { code: '+223', label: 'Mali' },
  { code: '+233', label: 'Ghana' },
  { code: '+234', label: 'Nigéria' },
  { code: '+237', label: 'Cameroun' },
  { code: '+33', label: 'France' },
];

const OTP_LENGTH = 6;

const inputClass =
  'w-full pl-10 pr-4 py-3 rounded-xl bg-[#0a0a0a] border border-[#252525] text-white placeholder-[#666666] focus:outline-none focus:ring-2 focus:ring-[#F97316] focus:border-transparent';

const RegisterPage = () => {
  const navigate = useNavigate();
  const {
    checkPhone,
    sendSmsOtp,
    confirmSmsOtp,
    sendWhatsappOtp,
    verifyWhatsappOtp,
    completeSignup,
  } = useAuth();

  const [step, setStep] = useState('phone');
  const [loading, setLoading] = useState(false);
  const [signupOpen, setSignupOpen] = useState(true);

  const [country, setCountry] = useState('+228');
  const [localNumber, setLocalNumber] = useState('');
  const [channel, setChannel] = useState(null);

  const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(''));
  const otpRefs = useRef([]);
  const [resendIn, setResendIn] = useState(0);

  const [info, setInfo] = useState({
    nom: '',
    genre: 'Homme',
    email: '',
    password: '',
    confirmPassword: '',
  });

  const fullPhone = `${country}${localNumber.replace(/\D/g, '')}`;

  // Les inscriptions peuvent être fermées depuis le backoffice, pour le site
  // comme pour l'appli (app_config.inscriptionOuverte)
  useEffect(() => {
    configAPI
      .get()
      .then(({ data }) => setSignupOpen(data.inscriptionOuverte))
      .catch(() => setSignupOpen(true));
  }, []);

  // Délai avant de pouvoir redemander un code
  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const timer = setTimeout(() => setResendIn((value) => value - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendIn]);

  /* ── Étape 1 : le numéro ─────────────────────────────────── */

  const handlePhoneSubmit = async (event) => {
    event.preventDefault();

    if (!signupOpen) {
      toast.error('Les inscriptions sont temporairement fermées. Réessayez plus tard.');
      return;
    }
    if (localNumber.replace(/\D/g, '').length < 6) {
      toast.error('Veuillez entrer un numéro valide');
      return;
    }

    setLoading(true);
    try {
      const exists = await checkPhone(fullPhone);
      if (exists) {
        setStep('existing');
        return;
      }
      setStep('channel');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Vérification impossible');
    } finally {
      setLoading(false);
    }
  };

  /* ── Étape 2 : le canal ──────────────────────────────────── */

  const handleChannel = async (selected) => {
    setChannel(selected);
    setLoading(true);

    const result = selected === 'whatsapp'
      ? await sendWhatsappOtp(fullPhone)
      : await sendSmsOtp(fullPhone, 'recaptcha-container');

    setLoading(false);

    if (!result.success) {
      toast.error(result.message || 'Envoi impossible');
      if (result.retryAfter) setResendIn(result.retryAfter);
      return;
    }

    setOtp(Array(OTP_LENGTH).fill(''));
    setResendIn(60);
    setStep('otp');
    toast.success(selected === 'whatsapp' ? 'Code envoyé sur WhatsApp' : 'Code envoyé par SMS');
  };

  /* ── Étape 3 : le code ───────────────────────────────────── */

  const handleOtpChange = (index, value) => {
    const digit = value.replace(/\D/g, '').slice(-1);
    const next = [...otp];
    next[index] = digit;
    setOtp(next);

    if (digit && index < OTP_LENGTH - 1) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index, event) => {
    if (event.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (event) => {
    const pasted = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);
    if (!pasted) return;
    event.preventDefault();
    const next = Array(OTP_LENGTH).fill('');
    pasted.split('').forEach((digit, index) => { next[index] = digit; });
    setOtp(next);
    otpRefs.current[Math.min(pasted.length, OTP_LENGTH - 1)]?.focus();
  };

  const handleOtpSubmit = async (event) => {
    event.preventDefault();
    const code = otp.join('');
    if (code.length < OTP_LENGTH) return;

    setLoading(true);
    const result = channel === 'whatsapp'
      ? await verifyWhatsappOtp(fullPhone, code)
      : await confirmSmsOtp(code);
    setLoading(false);

    if (!result.success) {
      toast.error(result.message || 'Code incorrect');
      setOtp(Array(OTP_LENGTH).fill(''));
      otpRefs.current[0]?.focus();
      return;
    }

    // Numéro finalement reconnu (compte créé entre-temps, ou via l'appli)
    if (result.existing) {
      toast.success('Ce numéro a déjà un compte, connectez-vous');
      navigate('/login', { state: { email: result.email } });
      return;
    }

    setStep('info');
  };

  /* ── Étape 4 : le profil ─────────────────────────────────── */

  const handleInfoSubmit = async (event) => {
    event.preventDefault();

    if (info.password !== info.confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    if (info.password.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    setLoading(true);
    const result = await completeSignup({
      nom: info.nom,
      genre: info.genre,
      email: info.email,
      password: info.password,
      telephone: fullPhone,
    });
    setLoading(false);

    if (result.success) navigate('/dashboard');
  };

  /* ── Rendu ───────────────────────────────────────────────── */

  const STEP_TITLES = {
    phone: { title: 'Créer un compte', subtitle: 'Commençons par votre numéro de téléphone' },
    existing: { title: 'Compte existant', subtitle: 'Ce numéro est déjà enregistré' },
    channel: { title: 'Recevoir le code', subtitle: `Où envoyer le code pour le ${fullPhone} ?` },
    otp: { title: 'Vérification', subtitle: `Entrez le code à ${OTP_LENGTH} chiffres reçu` },
    info: { title: 'Vos informations', subtitle: 'Dernière étape avant de commencer' },
  };

  const heading = STEP_TITLES[step];

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0a0a0a] py-12 px-4">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#F97316]/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#F97316]/5 rounded-full blur-3xl" />
      </div>

      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="max-w-md w-full bg-[#111111] rounded-2xl border border-[#252525] shadow-2xl p-8 relative z-10"
      >
        {/* Conteneur exigé par le reCAPTCHA invisible de Firebase Phone Auth */}
        <div id="recaptcha-container" />

        {step !== 'phone' && (
          <button
            type="button"
            onClick={() => setStep(step === 'info' ? 'otp' : step === 'otp' ? 'channel' : 'phone')}
            className="flex items-center gap-2 text-[#AAAAAA] hover:text-white transition mb-4 text-sm"
          >
            <ArrowLeftIcon className="w-4 h-4" />
            Retour
          </button>
        )}

        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#F97316]/10 rounded-2xl mb-4">
            <UserIcon className="w-8 h-8 text-[#F97316]" />
          </div>
          <h2 className="text-3xl font-display font-bold text-white">{heading.title}</h2>
          <p className="text-[#AAAAAA] mt-2">{heading.subtitle}</p>
        </div>

        {!signupOpen && step === 'phone' && (
          <div className="mb-6 p-4 rounded-xl bg-[#F97316]/10 border border-[#F97316]/30 text-[#F97316] text-sm">
            Les inscriptions sont temporairement fermées. Réessayez plus tard.
          </div>
        )}

          {/* Étape 1 — numéro */}
          {step === 'phone' && (
            <motion.form
              key="phone"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              onSubmit={handlePhoneSubmit}
              className="space-y-5"
            >
              <div>
                <label className="block text-sm font-medium text-[#CCCCCC] mb-2">Téléphone</label>
                <div className="flex gap-2">
                  <select
                    value={country}
                    onChange={(event) => setCountry(event.target.value)}
                    className="px-3 py-3 rounded-xl bg-[#0a0a0a] border border-[#252525] text-white focus:outline-none focus:ring-2 focus:ring-[#F97316]"
                  >
                    {COUNTRY_CODES.map((entry) => (
                      <option key={entry.code} value={entry.code}>
                        {entry.code}
                      </option>
                    ))}
                  </select>
                  <div className="relative flex-1">
                    <PhoneIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#666666]" />
                    <input
                      type="tel"
                      required
                      autoFocus
                      value={localNumber}
                      onChange={(event) => setLocalNumber(event.target.value)}
                      className={inputClass}
                      placeholder="90 00 00 00"
                    />
                  </div>
                </div>
                <p className="text-xs text-[#666666] mt-2">
                  Le même numéro que sur l'application VADOM, si vous l'utilisez déjà.
                </p>
              </div>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                type="submit"
                disabled={loading || !signupOpen}
                className="w-full bg-[#F97316] text-white py-3 rounded-xl font-semibold text-lg hover:bg-[#e8630a] transition-all shadow-lg shadow-[#F97316]/25 disabled:opacity-50"
              >
                {loading ? 'Vérification…' : 'Continuer'}
              </motion.button>
            </motion.form>
          )}

          {/* Numéro déjà connu */}
          {step === 'existing' && (
            <motion.div
              key="existing"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="space-y-4"
            >
              <p className="text-[#AAAAAA] text-center">
                Le {fullPhone} est déjà rattaché à un compte VADOM.
              </p>
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="w-full bg-[#F97316] text-white py-3 rounded-xl font-semibold hover:bg-[#e8630a] transition"
              >
                Se connecter
              </button>
              <button
                type="button"
                onClick={() => setStep('phone')}
                className="w-full bg-[#1a1a1a] text-white py-3 rounded-xl font-medium border border-[#252525] hover:bg-[#252525] transition"
              >
                Utiliser un autre numéro
              </button>
            </motion.div>
          )}

          {/* Étape 2 — canal */}
          {step === 'channel' && (
            <motion.div
              key="channel"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="space-y-3"
            >
              <button
                type="button"
                disabled={loading}
                onClick={() => handleChannel('whatsapp')}
                className="w-full flex items-center gap-4 p-4 rounded-xl bg-[#1a1a1a] border border-[#252525] hover:border-[#F97316] transition text-left disabled:opacity-50"
              >
                <div className="w-12 h-12 rounded-xl bg-[#25D366]/10 flex items-center justify-center shrink-0">
                  <ChatBubbleLeftRightIcon className="w-6 h-6 text-[#25D366]" />
                </div>
                <div>
                  <p className="text-white font-semibold">WhatsApp</p>
                  <p className="text-sm text-[#AAAAAA]">Recevoir le code sur WhatsApp</p>
                </div>
              </button>

              <button
                type="button"
                disabled={loading}
                onClick={() => handleChannel('sms')}
                className="w-full flex items-center gap-4 p-4 rounded-xl bg-[#1a1a1a] border border-[#252525] hover:border-[#F97316] transition text-left disabled:opacity-50"
              >
                <div className="w-12 h-12 rounded-xl bg-[#F97316]/10 flex items-center justify-center shrink-0">
                  <DevicePhoneMobileIcon className="w-6 h-6 text-[#F97316]" />
                </div>
                <div>
                  <p className="text-white font-semibold">SMS</p>
                  <p className="text-sm text-[#AAAAAA]">Recevoir le code par message</p>
                </div>
              </button>

              {loading && <p className="text-center text-[#AAAAAA] text-sm pt-2">Envoi en cours…</p>}
            </motion.div>
          )}

          {/* Étape 3 — code */}
          {step === 'otp' && (
            <motion.form
              key="otp"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              onSubmit={handleOtpSubmit}
              className="space-y-6"
            >
              <div className="flex justify-center gap-2" onPaste={handleOtpPaste}>
                {otp.map((digit, index) => (
                  <input
                    // eslint-disable-next-line react/no-array-index-key
                    key={index}
                    ref={(element) => { otpRefs.current[index] = element; }}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    autoFocus={index === 0}
                    onChange={(event) => handleOtpChange(index, event.target.value)}
                    onKeyDown={(event) => handleOtpKeyDown(index, event)}
                    className="w-12 h-14 text-center text-2xl font-bold rounded-xl bg-[#0a0a0a] border border-[#252525] text-white focus:outline-none focus:ring-2 focus:ring-[#F97316] focus:border-transparent"
                  />
                ))}
              </div>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                type="submit"
                disabled={loading || otp.join('').length < OTP_LENGTH}
                className="w-full bg-[#F97316] text-white py-3 rounded-xl font-semibold text-lg hover:bg-[#e8630a] transition-all shadow-lg shadow-[#F97316]/25 disabled:opacity-50"
              >
                {loading ? 'Vérification…' : 'Vérifier'}
              </motion.button>

              <div className="text-center">
                {resendIn > 0 ? (
                  <p className="text-sm text-[#666666]">Nouveau code dans {resendIn}s</p>
                ) : (
                  <button
                    type="button"
                    onClick={() => handleChannel(channel)}
                    className="text-sm text-[#F97316] hover:underline"
                  >
                    Renvoyer le code
                  </button>
                )}
              </div>
            </motion.form>
          )}

          {/* Étape 4 — profil */}
          {step === 'info' && (
            <motion.form
              key="info"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              onSubmit={handleInfoSubmit}
              className="space-y-5"
            >
              <div>
                <label className="block text-sm font-medium text-[#CCCCCC] mb-2">Nom complet</label>
                <div className="relative">
                  <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#666666]" />
                  <input
                    type="text"
                    required
                    autoFocus
                    value={info.nom}
                    onChange={(event) => setInfo({ ...info, nom: event.target.value })}
                    className={inputClass}
                    placeholder="Kodjo Mensah"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#CCCCCC] mb-2">Genre</label>
                <div className="grid grid-cols-2 gap-3">
                  {['Homme', 'Femme'].map((option) => (
                    <button
                      key={option}
                      type="button"
                      onClick={() => setInfo({ ...info, genre: option })}
                      className={`py-3 rounded-xl font-medium transition-all ${
                        info.genre === option
                          ? 'bg-[#F97316] text-white shadow-lg shadow-[#F97316]/25'
                          : 'bg-[#1a1a1a] text-[#AAAAAA] border border-[#252525] hover:bg-[#252525]'
                      }`}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#CCCCCC] mb-2">Email</label>
                <div className="relative">
                  <EnvelopeIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#666666]" />
                  <input
                    type="email"
                    required
                    value={info.email}
                    onChange={(event) => setInfo({ ...info, email: event.target.value })}
                    className={inputClass}
                    placeholder="votre@email.com"
                  />
                </div>
                <p className="text-xs text-[#666666] mt-2">
                  Il vous servira à vous connecter, ici comme dans l'application.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#CCCCCC] mb-2">Mot de passe</label>
                <div className="relative">
                  <LockClosedIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#666666]" />
                  <input
                    type="password"
                    required
                    minLength={6}
                    value={info.password}
                    onChange={(event) => setInfo({ ...info, password: event.target.value })}
                    className={inputClass}
                    placeholder="••••••••"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#CCCCCC] mb-2">
                  Confirmer le mot de passe
                </label>
                <div className="relative">
                  <LockClosedIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#666666]" />
                  <input
                    type="password"
                    required
                    value={info.confirmPassword}
                    onChange={(event) => setInfo({ ...info, confirmPassword: event.target.value })}
                    className={inputClass}
                    placeholder="••••••••"
                  />
                </div>
              </div>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                type="submit"
                disabled={loading}
                className="w-full bg-[#F97316] text-white py-3 rounded-xl font-semibold text-lg hover:bg-[#e8630a] transition-all shadow-lg shadow-[#F97316]/25 disabled:opacity-50"
              >
                {loading ? 'Création…' : 'Créer mon compte'}
              </motion.button>
            </motion.form>
          )}

        {step === 'phone' && (
          <div className="mt-6 text-center">
            <p className="text-[#AAAAAA]">
              Déjà un compte ?{' '}
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="text-[#F97316] font-semibold hover:underline"
              >
                Se connecter
              </button>
            </p>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default RegisterPage;
